import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, User, Check, SpinnerGap, Camera } from '@phosphor-icons/react';
import type { Profile } from '../types';
import * as api from '../api/client';
import McpTokensPanel from './settings/McpTokensPanel';
import MyMemoryPanel from './settings/MyMemoryPanel';
import WorkspaceMembersPanel from './settings/WorkspaceMembersPanel';
import KnowledgePanel from './KnowledgePanel';

interface SettingsPageProps {
  username: string;
  profile: Profile | null;
  workspaceId: string | null;
  workspaceName: string | null;
  onBack: () => void;
  onProfileUpdate: (profile: Profile) => void;
}

const AVATAR_COLORS = [
  '#0033FF', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#ef4444', '#f97316', '#FF6B00',
  '#eab308', '#22c55e', '#16a34a', '#14b8a6',
  '#06b6d4', '#3b82f6', '#64748b', '#1e293b',
];

export default function SettingsPage({
  username,
  profile,
  workspaceId,
  workspaceName,
  onBack,
  onProfileUpdate,
}: SettingsPageProps) {
  const [displayName, setDisplayName] = useState(profile?.display_name || username);
  const [avatarColor, setAvatarColor] = useState(profile?.avatar_color || '#0033FF');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || username);
      setAvatarColor(profile.avatar_color || '#0033FF');
      setAvatarUrl(profile.avatar_url || '');
    }
  }, [profile, username]);

  const hasChanges = (displayName !== (profile?.display_name || username))
    || (avatarColor !== (profile?.avatar_color || '#0033FF'));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.updateMyProfile({
        display_name: displayName,
        avatar_color: avatarColor,
      } as Partial<Profile>);
      onProfileUpdate(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB');
      return;
    }
    setUploading(true);
    try {
      const updated = await api.uploadAvatar(file);
      setAvatarUrl(updated.avatar_url || '');
      onProfileUpdate(updated);
    } catch (err: any) {
      alert(err.message || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '20px 24px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)',
    border: '1px solid var(--border)', borderRadius: 8,
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s',
    boxSizing: 'border-box' as const,
  };

  const color = avatarColor || '#0033FF';

  return (
    <div style={{ height: 'var(--app-height)', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header style={{
        height: 48, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center',
        padding: '0 16px', flexShrink: 0, background: 'var(--bg-surface)', gap: 10,
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', transition: 'all 0.15s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'var(--code-bg)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          <ArrowLeft size={15} weight="light" />
        </button>
        <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-primary)' }}>Settings</span>
        {workspaceName && (
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
            {workspaceName}
          </span>
        )}
      </header>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '32px 48px', maxWidth: 560, margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Profile Section */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <User size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>Profile</span>
            </div>

            {/* Avatar + Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div
                style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => fileInputRef.current?.click()}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    style={{
                      width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                      border: `2px solid ${color}`,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', background: color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 'var(--fs-xl)', fontWeight: 700, color,
                    border: `2px solid ${color}20`,
                    transition: 'all 0.2s',
                  }}>
                    {(displayName || '?')[0].toUpperCase()}
                  </div>
                )}
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.15s',
                }}
                  onMouseOver={e => { e.currentTarget.style.opacity = '1'; }}
                  onMouseOut={e => { e.currentTarget.style.opacity = '0'; }}
                >
                  {uploading
                    ? <SpinnerGap size={18} weight="light" className="animate-spin" style={{ color: '#fff' }} />
                    : <Camera size={18} weight="light" style={{ color: '#fff' }} />
                  }
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {displayName || username}
                </div>
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                  @{username}
                </div>
              </div>
            </div>

            {/* Display Name */}
            <div style={{ marginBottom: 16 }}>
              <div style={labelStyle}>Display Name</div>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = '#0033FF'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
            </div>

            {/* Avatar Color */}
            <div>
              <div style={labelStyle}>Avatar Color</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {AVATAR_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setAvatarColor(c)}
                    style={{
                      width: 28, height: 28, borderRadius: '50%', background: c,
                      border: avatarColor === c ? '2px solid var(--text-primary)' : '2px solid transparent',
                      cursor: 'pointer', transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      outline: avatarColor === c ? '2px solid var(--bg-surface)' : 'none',
                      outlineOffset: -4,
                    }}
                    onMouseOver={e => { if (avatarColor !== c) e.currentTarget.style.transform = 'scale(1.15)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    {avatarColor === c && <Check size={14} weight="bold" style={{ color: '#fff' }} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Save button */}
            {hasChanges && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '8px 20px', fontSize: 'var(--fs-sm)', fontWeight: 600,
                    background: '#0033FF', color: 'white', border: 'none', borderRadius: 8,
                    cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 6,
                    opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
                  }}
                >
                  {saving ? <SpinnerGap size={14} weight="light" className="animate-spin" /> : null}
                  {saved ? 'Saved' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <WorkspaceMembersPanel workspaceId={workspaceId} />

          {/* Knowledge sources — Confluence + Droploft connections
              centralised in Settings so the user doesn't have to
              hunt for them inside a deck. Aligns PP main KnowledgePanel
              integration. */}
          <KnowledgePanel />

          {/* My Memory — user-scope notes the agent reads alongside
              the deck's task memory. Aligns PP main /dev branch
              feat/pp-consume-sdk MyMemoryPanel. */}
          {profile?.id && <MyMemoryPanel userId={profile.id} />}

          {/* MCP Personal Access Tokens — aligns PP main #135 */}
          <McpTokensPanel />

        </div>
      </div>
    </div>
  );
}
